
/**
 * Early initialization for React scheduler functions
 * This file is imported before any other React code to ensure scheduler patches are available
 */

// Define the Ho function and other scheduler functions that might be missing
const schedulerFunctions = {
  // Known missing functions from the errors
  Ho: function(...args: any[]) {
    console.log('Polyfilled Ho function called with:', args);
    return 0;
  },
  
  // Generic single-letter functions that might be used in minified code
  o: function(...args: any[]) {
    console.log('Polyfilled o function called with:', args);
    return 0;
  },
  
  // FT might call these functions
  FT: function(...args: any[]) {
    console.log('Polyfilled FT function called with:', args);
    return 0;
  },
  
  wE: function(...args: any[]) {
    console.log('Polyfilled wE function called with:', args);
    return 0;
  },
  
  // Common scheduler functions
  unstable_scheduleCallback: function(priorityLevel: number, callback: Function, options?: { delay?: number }) {
    console.log('Polyfilled unstable_scheduleCallback called');
    setTimeout(() => {
      try {
        callback();
      } catch (e) {
        console.error('Error in scheduler callback:', e);
      }
    }, options?.delay || 0);
    return { id: Math.random() };
  },
  
  unstable_cancelCallback: function(callbackNode: any) {
    console.log('Polyfilled unstable_cancelCallback called');
    // No-op implementation
  },
  
  unstable_shouldYield: function() {
    // Return false to indicate that we don't need to yield
    return false;
  },
  
  unstable_requestPaint: function() {
    // No-op implementation
  },
  
  unstable_now: function() {
    return performance.now();
  },
  
  unstable_getCurrentPriorityLevel: function() {
    // Return normal priority
    return 3; // Normal priority in scheduler
  },
  
  unstable_ImmediatePriority: 1,
  unstable_UserBlockingPriority: 2,
  unstable_NormalPriority: 3,
  unstable_LowPriority: 4,
  unstable_IdlePriority: 5,
  
  // Add a fallback function handler for any undefined scheduler function
  __scheduler_fallback: function(name: string, ...args: any[]) {
    console.log(`Fallback called for undefined scheduler function: ${name} with args:`, args);
    return 0;
  }
};

// Apply patches to global scope
export function applyEarlySchedulerPatches() {
  if (typeof window !== 'undefined') {
    console.log('Applying early React scheduler patches');
    
    // Method 1: Add to window directly
    Object.entries(schedulerFunctions).forEach(([key, value]) => {
      (window as any)[key] = value;
    });
    
    // Method 2: Create global scheduler object
    (window as any).scheduler = {
      ...(window as any).scheduler,
      ...schedulerFunctions,
    };
    
    // Method 3: Patch specific vendor property paths that might be used
    try {
      const vendorGlobals = ['$E', '__REACT_SCHEDULER__', 'ReactDOM', 'ReactScheduler'];
      vendorGlobals.forEach(globalName => {
        if ((window as any)[globalName]) {
          Object.entries(schedulerFunctions).forEach(([key, value]) => {
            if (typeof (window as any)[globalName][key] !== 'function') {
              (window as any)[globalName][key] = value;
            }
          });
        }
      });
    } catch (e) {
      console.warn('Could not patch vendor globals:', e);
    }
    
    // Setup proxy handler for window to catch all function access attempts
    try {
      const originalWindow = window;
      const windowProxy = new Proxy(window, {
        get(target, prop) {
          const value = (target as any)[prop];
          if (typeof prop === 'string' && typeof value === 'undefined' && typeof schedulerFunctions.__scheduler_fallback === 'function') {
            console.log(`Window proxy intercepted call to undefined function: ${String(prop)}`);
            return (...args: any[]) => schedulerFunctions.__scheduler_fallback(String(prop), ...args);
          }
          return value;
        }
      });
      
      // We can't replace the global window object, but we can try to patch specific elements
      if ((window as any).React && !(window as any).React.__patched) {
        (window as any).React = new Proxy((window as any).React, {
          get(target, prop) {
            const value = (target as any)[prop];
            if (typeof prop === 'string' && typeof value === 'undefined' && typeof schedulerFunctions.__scheduler_fallback === 'function') {
              console.log(`React proxy intercepted call to undefined function: ${String(prop)}`);
              return (...args: any[]) => schedulerFunctions.__scheduler_fallback(String(prop), ...args);
            }
            return value;
          }
        });
        (window as any).React.__patched = true;
      }
    } catch (e) {
      console.warn('Could not set up window proxy:', e);
    }
    
    // Create ReactSchedulerPolyfill for errorHandling.ts to use 
    // Only set if not already defined
    if (!window.ReactSchedulerPolyfill) {
      window.ReactSchedulerPolyfill = {
        scheduleCallback: (callback: () => void) => {
          console.log('ReactSchedulerPolyfill.scheduleCallback called');
          return +setTimeout(() => {
            try {
              callback();
            } catch (e) {
              console.error('Error in scheduler callback:', e);
              if (window.captureError) {
                window.captureError(e);
              }
            }
          }, 0);
        },
        cancelCallback: (callbackId: number) => {
          clearTimeout(callbackId);
        }
      };
    }
    
    console.log('Early React scheduler patches applied');
  }
}

// Run patches immediately when file is loaded
applyEarlySchedulerPatches();

// Import type definition from the new common globals file
export default applyEarlySchedulerPatches;

